import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { searchClients } from '@/features/clients/api/clients';
import {
  getSmsCampaign,
  saveSmsCampaign,
  sendSmsCampaign,
} from '@/features/communications/api/smsCampaigns';

export function SmsCampaignPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { campaignId } = useParams();
  const id = campaignId ? Number(campaignId) : undefined;
  const [params] = useSearchParams();
  const initialClientIds = useMemo(
    () => (params.get('clients') ?? '').split(',').map(Number).filter((value) => value > 0),
    [params],
  );
  const [message, setMessage] = useState('');
  const [clientIds, setClientIds] = useState<number[]>(initialClientIds);
  const [savedSnapshot, setSavedSnapshot] = useState({ message: '', clientIds: [] as number[] });
  const [loadedId, setLoadedId] = useState<number | undefined>();
  const clients = useQuery({ queryKey: ['clients', 'sms-options'], queryFn: () => searchClients() });
  const campaign = useQuery({
    queryKey: ['sms-campaign', id],
    queryFn: () => getSmsCampaign(id as number),
    enabled: Boolean(id),
    refetchInterval: id ? 2000 : false,
  });

  useEffect(() => {
    if (!campaign.data || loadedId === campaign.data.id) return;
    const ids = campaign.data.recipients.map((recipient) => recipient.client_id);
    setMessage(campaign.data.message);
    setClientIds(ids);
    setSavedSnapshot({ message: campaign.data.message, clientIds: ids });
    setLoadedId(campaign.data.id);
  }, [campaign.data, loadedId]);

  const dirty =
    message !== savedSnapshot.message ||
    JSON.stringify([...clientIds].sort()) !== JSON.stringify([...savedSnapshot.clientIds].sort());
  const selectedClients = (clients.data ?? []).filter((client) => clientIds.includes(client.id));
  const save = useMutation({
    mutationFn: () => saveSmsCampaign(message.trim(), clientIds, id),
    onSuccess: (saved) => {
      const ids = saved.recipients.map((recipient) => recipient.client_id);
      setSavedSnapshot({ message: saved.message, clientIds: ids });
      queryClient.setQueryData(['sms-campaign', saved.id], saved);
      if (!id) void navigate(`/communications/sms/${saved.id}`, { replace: true });
    },
  });
  const send = useMutation({
    mutationFn: () => sendSmsCampaign(id as number),
    onSuccess: (result) => queryClient.setQueryData(['sms-campaign', result.id], result),
  });
  const status = campaign.data?.status ?? 'draft';
  const canSend = Boolean(id) && !dirty && ['draft', 'failed', 'partial'].includes(status);
  const canEdit = ['draft', 'failed', 'partial'].includes(status);
  const recipients = (campaign.data?.recipients ?? selectedClients.map((client) => ({
    id: client.id,
    client_id: client.id,
    client_name: client.name,
    phone: null,
    status: 'pending',
    error_message: null,
    sent_at: null,
  }))).filter((recipient) => clientIds.includes(recipient.client_id));

  return (
    <Box sx={{ p: 1 }}>
      <Paper elevation={0} sx={paperSx}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minHeight: 46, px: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => void navigate('/clients')} sx={commandSx}>
            Back
          </Button>
          <Divider orientation="vertical" flexItem sx={{ my: 1 }} />
          <Button
            startIcon={<SaveOutlinedIcon />}
            disabled={!dirty || !message.trim() || clientIds.length === 0 || save.isPending}
            onClick={() => save.mutate()}
            sx={commandSx}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button
            startIcon={<SendOutlinedIcon />}
            disabled={!canSend || send.isPending}
            onClick={() => send.mutate()}
            sx={commandSx}
          >
            {send.isPending || status === 'queued' || status === 'sending' ? 'Sending…' : 'Send'}
          </Button>
        </Stack>
      </Paper>

      <Paper elevation={0} sx={{ ...paperSx, mt: 1.5, p: 2 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h1">SMS message</Typography>
            <Typography color="text.secondary">One tracked message will be sent to each selected client.</Typography>
          </Box>
          <StatusChip status={status} />
        </Stack>
        {(save.isError || send.isError) && <Alert severity="error" sx={{ mb: 2 }}>The message could not be saved or sent. Please try again.</Alert>}
        <TextField
          fullWidth
          multiline
          minRows={5}
          label="Message"
          value={message}
          onChange={(event) => setMessage(event.target.value.slice(0, 1600))}
          helperText={`${message.length}/1600 characters`}
          disabled={!canEdit}
        />
      </Paper>

      <Paper elevation={0} sx={{ ...subgridPaperSx, mt: 1.5 }}>
        <Stack direction="row" alignItems="center" sx={subgridCommandSx}>
          <Typography variant="h2" sx={{ px: 1.5 }}>Recipients ({recipients.length})</Typography>
        </Stack>
        {(clients.isLoading || campaign.isLoading) ? (
          <Box sx={{ p: 2 }}><CircularProgress size={24} /></Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ minWidth: 760 }} role="table" aria-label="SMS recipients">
              <Box sx={recipientGridHeaderSx} role="row">
                <Box aria-hidden="true" />
                <Typography fontWeight={600} role="columnheader">Client</Typography>
                <Typography fontWeight={600} role="columnheader">Mobile</Typography>
                <Typography fontWeight={600} role="columnheader">Status</Typography>
                <Typography fontWeight={600} role="columnheader">Details</Typography>
              </Box>
              {recipients.map((recipient) => (
                <Box
                  key={recipient.id}
                  role="row"
                  tabIndex={0}
                  onClick={() => void navigate(`/clients/${recipient.client_id}`)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      void navigate(`/clients/${recipient.client_id}`);
                    }
                  }}
                  sx={recipientGridRowSx}
                >
                  <Checkbox
                    size="small"
                    checked
                    disabled={!canEdit}
                    inputProps={{ 'aria-label': `Select ${recipient.client_name}` }}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => setClientIds((current) => current.filter((clientId) => clientId !== recipient.client_id))}
                  />
                  <Link
                    component="button"
                    type="button"
                    underline="none"
                    textAlign="left"
                    onClick={(event) => {
                      event.stopPropagation();
                      void navigate(`/clients/${recipient.client_id}`);
                    }}
                  >
                    {recipient.client_name}
                  </Link>
                  <Typography>{recipient.phone ?? (id ? 'No SMS-enabled mobile' : 'Checked when saved')}</Typography>
                  <Box><StatusChip status={recipient.status} /></Box>
                  <Typography>{recipient.error_message ?? '—'}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
        <Typography variant="body2" sx={{ pt: 1.5 }}>
          Rows: {recipients.length}
        </Typography>
      </Paper>
    </Box>
  );
}

function StatusChip({ status }: { status: string }) {
  const color = status === 'delivered' || status === 'sent' ? 'success' : status === 'failed' ? 'error' : status === 'partial' ? 'warning' : 'default';
  return <Chip size="small" label={status.charAt(0).toUpperCase() + status.slice(1)} color={color} />;
}

const paperSx = { border: '1px solid', borderColor: 'divider', borderRadius: 1.5, boxShadow: '0 2px 6px rgba(0,0,0,.12)' };
const commandSx = { color: 'text.primary', '& .MuiButton-startIcon': { color: 'primary.main' } };
const recipientGridColumns = '42px minmax(190px, 1.2fr) minmax(180px, 1fr) 150px minmax(180px, 1fr)';
const recipientGridHeaderSx = {
  display: 'grid',
  gridTemplateColumns: recipientGridColumns,
  alignItems: 'center',
  minHeight: 44,
  borderBottom: '1px solid #e1dfdd',
  gap: 1,
};
const recipientGridRowSx = {
  display: 'grid',
  gridTemplateColumns: recipientGridColumns,
  alignItems: 'center',
  minHeight: 48,
  borderBottom: '1px solid #edebe9',
  gap: 1,
  cursor: 'pointer',
  '&:hover': { bgcolor: '#f3f2f1' },
  '&:focus-visible': {
    outline: '2px solid',
    outlineColor: 'primary.main',
    outlineOffset: -2,
  },
};
const subgridPaperSx = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1.5,
  boxShadow: '0 2px 6px rgba(0,0,0,.12)',
  p: 1.5,
};
const subgridCommandSx = { minHeight: 44, borderBottom: '1px solid #e1dfdd' };



